import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NormalizedAssignment } from '@scale/shared/assignmentJson/legacy/types/NormalizedAssignment';
import { AuditResult, FlagAuditResult } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import {
  AuditLookup,
  AuditLookupType,
  SpeedAuditLookupResponse,
} from '@scale/shared/types/corp/attemptAudit';
import { PerAnnotationError } from '@scale/shared/types/customerAudit';
import { Button, makeStyles } from 'design-system';
import React, { useMemo, useState } from 'react';
// eslint-disable-next-line restrict-imports/restrict-imports
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { CompleteSpeedAuditModal } from 'react-scale/components/audits/SpeedAudit/v2/CompleteSpeedAuditModal';
import { useQAOperationContext } from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';
import { ChatLayoutConfigButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatLayoutConfigButton';
import LookupObjectLink from 'react-scale/corp/components/Lookup/LookupObjectLink';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { useAsync } from 'react-use-v13';

function SpeedAuditHeaderV2(props: {
  styles: ClassNameMap<any>;
  name: string;
  id: string;
  openKeyboardShortcuts: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  headerId?: string;
  flagAudit?: boolean;
  subtaskAssignment: {
    auditLookup?: AuditLookup;
    subtask?: NormalizedAssignment;
    responses?: SpeedAuditLookupResponse[];
    attemptAuditId?: string;
    result?: AuditResult;
    flagResult?: FlagAuditResult;
    comments?: string;
    taskInstructionItems?: string[];
    perAnnotationInstructionItems?: PerAnnotationError[];
  };
  auditLookup: { id?: string; type?: AuditLookupType };
  index: number;
  numObjects: number;
  numSubtaskAssignments: number;
  completed: boolean;
  missingRequiredGrade: boolean;
  onRestart: () => void;
  projectId: string;
  metadataDescription: undefined | JSX.Element;
  completeAudit: (sendFeedback: boolean) => void;
  showResults: () => void;
}) {
  const classes = useStyles();
  const { qaOperation, isInReview } = useQAOperationContext();
  const [isCompleting, setIsCompleting] = useState(false);

  const displayOptionsButton = useMemo(() => {
    if (props.subtaskAssignment.subtask?.type === TaskType.Chat) {
      return (
        <div className="inline scaleui scale-tailwind scale-theme mr-2">
          <ChatLayoutConfigButton buttonText="Display Options" multicolumnEnabled />
        </div>
      );
    }
    return null;
  }, [props.subtaskAssignment.subtask?.type]);

  const { value: bmAccuracy } = useAsync(async () => {
    if (props.auditLookup.type === 'trainingattempt' && props.auditLookup.id) {
      const { accuracy } = await apiFetch(null, '/corp-api/attemptAudit/bmAccuracy', {
        method: 'GET',
        query: { attemptId: props.auditLookup.id },
      });
      return accuracy;
    }
    return null;
  }, [props.auditLookup.type, props.auditLookup.id]);

  return (
    <div
      style={{
        display: 'flex',
        flex: '1 1 0',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '10px 20px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            fontSize: 'x-large',
          }}
        >
          <span className={classes.title}>Speed Audit 2.0</span>
          <span>{props.name.length < 25 ? props.name : props.name.slice(0, 22) + '..'}</span>
          <a
            className={props.styles.helpLink}
            href="#"
            role="button"
            onClick={props.openKeyboardShortcuts}
          >
            <HelpOutlineIcon width="16" height="16" fill="currentColor" />
            <span className={props.styles.helpLinkText}>Keyboard Shortcuts</span>
          </a>
        </div>
        <div style={{ flexGrow: 1 }} />
        {props.subtaskAssignment?.subtask && !props.subtaskAssignment?.subtask.isSfuTask && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div>
              {props.auditLookup.type === 'trainingattempt' ? 'Training attempt' : 'Subtask'}{' '}
              <LookupObjectLink
                shouldOpenNewTab
                objectId={props.subtaskAssignment.subtask?._id ?? ''}
              />
            </div>
            <div>
              Task{' '}
              <LookupObjectLink
                shouldOpenNewTab
                objectId={props.subtaskAssignment.subtask?.task ?? ''}
              />
            </div>
            {bmAccuracy !== null && <div>Training attempt accuracy: {bmAccuracy}</div>}
          </div>
        )}
        {props.subtaskAssignment?.subtask && props.subtaskAssignment?.subtask.isSfuTask && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div>
              HumanNodeId{' '}
              <LookupObjectLink
                shouldOpenNewTab
                objectId={props.subtaskAssignment.subtask?._id ?? ''}
              />
            </div>
            <div>
              TaskId{' '}
              <LookupObjectLink
                shouldOpenNewTab
                objectId={props.subtaskAssignment.subtask?.task ?? ''}
              />
            </div>
            {bmAccuracy !== null && <div>Training attempt accuracy: {bmAccuracy}</div>}
          </div>
        )}
        <div style={{ flexGrow: 1 }} />
        <div>
          {displayOptionsButton}
          <Button
            onClick={props.showResults}
            startIcon={<FontAwesomeIcon icon="chart-line" />}
            type="primary"
            style={{ marginRight: '10px' }}
          >
            View Results
          </Button>
          {(!qaOperation || !isInReview()) && (
            <Button
              onClick={() => setIsCompleting(true)}
              startIcon={<FontAwesomeIcon icon="check" />}
              type="primary"
            >
              Complete Audit
            </Button>
          )}
        </div>
      </div>
      <CompleteSpeedAuditModal
        isCompleteAuditModalOpen={isCompleting}
        setCompleteAuditModalOpen={setIsCompleting}
        speedAuditBatch={props.name}
        completeAudit={props.completeAudit}
      />
    </div>
  );
}

export default SpeedAuditHeaderV2;

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 'bold',
    marginRight: '10px',
  },
}));

function HelpOutlineIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" {...props}>
      <path fill="none" d="M0 0h24v24H0z" />
      <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" />
    </svg>
  );
}
